/**
 * Connector Mappings by Signal Type
 * 
 * Maps signal types to their relevant physical connector types.
 * Used to filter the connector dropdown when editing port groups.
 * 
 * Edit this file to add/remove connector associations.
 * If a signal type is not listed, all connectors will be shown.
 */

export const connectorsBySignalType = {
    // Standard analog audio - most common connectors
    'audio': [
        'xlr-m',      // XLR Male (typical output)
        'xlr-f',      // XLR Female (typical input)
        'trs',        // 1/4" TRS (balanced)
        'ts',         // 1/4" TS (unbalanced)
        '3.5mm',      // 3.5mm / 1/8"
        'combo',      // Combo jack (XLR + 1/4")
        'rca',        // RCA (consumer)
        'speakon',    // Speakon (PA/speakers)
        'euroblock',  // Euroblock/Phoenix
        'db25',       // DB25 (studio patchbays)
        'd-sub',      // D-sub alias
        'db9',        // DB9
        'bnc',        // BNC (broadcast)
    ],

    // Traditional MIDI - DIN and TRS
    'midi': [
        'midi-din',   // 5-pin DIN
        '3.5mm',      // TRS MIDI (modern synths)
    ],

    // Network/RTP MIDI
    'midi-rtp': [
        'rj45',       // Ethernet
    ],

    // USB MIDI Host (accepts USB devices)
    'midi-usb-host': [
        'usb-a',      // Host port (accepts devices)
    ],

    // USB MIDI Device (connects to hosts)
    'midi-usb-device': [
        'usb-b',      // Standard USB-B
        'usb-c',      // USB-C
        'usb-mini',   // USB Mini
        'usb-micro',  // USB Micro
    ],

    // Control Voltage (modular)
    'cv': [
        '3.5mm',      // Eurorack standard
        'trs',        // 1/4" modular
        'ts',         // 1/4" TS
        'euroblock',  // Euroblock
    ],

    // Gate signals (modular)
    'gate': [
        '3.5mm',      // Eurorack standard
        'trs',        // 1/4" modular
        'ts',         // 1/4" TS
        'euroblock',  // Euroblock
    ],

    // Clock signals (modular/sync)
    'clock': [
        '3.5mm',      // Eurorack standard
        'trs',        // 1/4" modular
        'ts',         // 1/4" TS
        'euroblock',  // Euroblock
        'bnc',        // BNC (video sync, word clock)
    ],

    // Digital audio - AES/EBU (professional)
    'aes-ebu': [
        'xlr-m',
        'xlr-f',
        'xlr-3',      // 3-pin XLR
    ],

    // Digital audio - S/PDIF (consumer)
    'spdif': [
        'rca',        // Coaxial S/PDIF
        'toslink',    // Optical S/PDIF
        'bnc',        // BNC (rare but used)
    ],

    // Digital audio - ADAT Lightpipe
    'adat': [
        'toslink',    // ADAT uses TOSLINK connectors
    ],

    // Digital audio - MADI
    'madi': [
        'bnc',        // Coax MADI
        'fiber-sc',   // Optical MADI
        'fiber-lc',   // Optical MADI (LC)
    ],

    // Network audio - Dante
    'dante': [
        'rj45',
        'ethercon',   // Rugged RJ45
    ],

    // Network audio - AES67
    'aes67': [
        'rj45',
        'ethercon',
    ],

    // Word Clock
    'word-clock': [
        'bnc',
    ],

    // DMX lighting control
    'dmx': [
        'xlr-5',      // 5-pin XLR (standard DMX)
        'xlr-3',      // 3-pin XLR (common variant)
        'rj45',       // DMX over Cat5
        'ethercon',
    ],
};

/**
 * Get connectors for a signal type (or all types if multi-type)
 * @param {string|string[]} signalType - Single type or array of types
 * @returns {string[]|null} Array of connector keys, or null for "show all"
 */
export function getConnectorsForSignalType(signalType) {
    // Handle array of types (multi-type)
    if (Array.isArray(signalType)) {
        if (signalType.length === 0) return null;
        if (signalType.length === 1) {
            return connectorsBySignalType[signalType[0]] || null;
        }
        // Multi-type: union of all connectors
        const connectorSet = new Set();
        for (const type of signalType) {
            const connectors = connectorsBySignalType[type];
            if (!connectors) {
                // Unknown type in array = show all
                return null;
            }
            connectors.forEach(c => connectorSet.add(c));
        }
        return [...connectorSet];
    }
    
    // Single type
    return connectorsBySignalType[signalType] || null;
}
